import React, { Suspense, lazy } from "react"
import "./assets/App.css"
import { BrowserRouter, Switch, Route } from "react-router-dom"
import { AuthProvider, useAuth } from "./context/AuthContext"
import { Helmet, HelmetProvider } from "react-helmet-async"
import Header from "./pages/Landingpage/Header"
import ContactButton from "./components/ContactButton"
import PrivateRoute from "./components/PrivateRoute"
import Hero from "./pages/Landingpage/Hero"

// Lazy load components
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"))
const Register = lazy(() => import("./pages/Sign/Register"))
const Login = lazy(() => import("./pages/Sign/Login"))
const ForgotPassword = lazy(() => import("./pages/Sign/ForgotPassword"))
const Pricing = lazy(() => import("./components/Pricing"))
const Examples = lazy(() => import("./pages/Landingpage/Examples"))
const CalltoAction = lazy(() => import("./pages/Landingpage/CalltoAction"))
const Popup = lazy(() => import("./pages/Sign/Popup"))
const FAQs = lazy(() => import("./components/Faq"))
const Docs = lazy(() => import("./pages/API/Docs"))
const ComfyUI = lazy(() => import("./pages/API/ComfyUI"))
const Admin = lazy(() => import("./pages/Dashboard/AdminShell"))
const AdminDashboard = lazy(() => import("./pages/Dashboard/AdminDashboard"))
const Stats = lazy(() => import("./pages/Dashboard/Stats"))
const Success = lazy(() => import("./pages/Dashboard/Success"))
const Features = lazy(() => import("./pages/Landingpage/Features"))
const Footer = lazy(() => import("./pages/Landingpage/Footer"))
const Billing = lazy(() => import("./pages/Other/Billing"))
const Support = lazy(() => import("./pages/Other/Support"))
const Profile = lazy(() => import("./pages/Other/Profile"))
const ImageConverter = lazy(() => import("./pages/Tools/ImageConverter"))
const ImageDescriptionGenerator = lazy(() =>
  import("./pages/Tools/ImageDescriptionGenerator")
)
const NotFound = lazy(() => import("./pages/Other/NotFound"))
const Terms = lazy(() => import("./pages/Other/Terms"))
const Privacy = lazy(() => import("./pages/Other/Privacy"))
const Subprocessors = lazy(() => import("./pages/Other/Subprocessors"))
const SpeedBenchmark = lazy(() => import("./pages/Benchmarks/SpeedBenchmark"))
const ResolutionBenchmark = lazy(() => import("./pages/Benchmarks/ResolutionBenchmark"))
const FluxUpscaler = lazy(() => import("./pages/Tools/FluxUpscaler"))
const ClarityProUpscaler = lazy(() =>
  import("./pages/Tools/ClarityProUpscaler")
)
const ClarityUpscaler = lazy(() => import("./pages/Tools/ClarityUpscaler"))
const CrystalUpscaler = lazy(() => import("./pages/Tools/CrystalUpscaler"))
const Feedback = lazy(() => import("./pages/Feedback"))
const VideoTest = lazy(() => import("./pages/Other/VideoTest"))
const Development = lazy(() => import("./pages/Landingpage/Development"))
const NewLanding = lazy(() =>
  import("./pages/Landingpage/Development").then((m) => ({
    default: m.NewLanding,
  }))
)
const AdminTools = lazy(() => import("./pages/Dashboard/AdminCredits"))

// Optimize loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
  </div>
)

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
          <Route exact path="/">
            <ContactButton />
          </Route>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  )
}

function AppContent() {
  const { showLoginPopup, setShowLoginPopup } = useAuth()

  React.useEffect(() => {
    // Only set the first landing page if it hasn't been set before
    if (!localStorage.getItem("first_landing_page")) {
      const path = window.location.pathname
      localStorage.setItem("first_landing_page", path)
    }
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-bg-color">
      <Suspense fallback={<LoadingFallback />}>
        <Switch>
          <Route exact path="/login">
            <Header />
            <Login />
          </Route>
          <Route exact path="/register">
            <Header />
            <Register />
          </Route>
          <Route exact path="/forgot-password">
            <Header />
            <ForgotPassword />
          </Route>
          <Route exact path="/">
            <Helmet>
              <title>Clarity AI - #1 AI Image Upscaler & Enhancer</title>
              <meta
                name="description"
                content="Upscale and enhance your images with the power of AI. Clarity AI is the #1 AI Image Upscaler & Enhancer with API support. Enlarge images and get HD quality."
              />
              <link rel="canonical" href="https://clarityai.co" />
            </Helmet>
            <Popup />
            <NewLanding />
          </Route>
          {/* Alte Landing Page als Archiv, nicht indexierbar */}
          <Route exact path="/legacy">
            <Helmet>
              <title>Clarity AI - Legacy</title>
              <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Header />
            <Popup />
            <Hero />
            <Features />
            <Examples />
            <FAQs />
            <CalltoAction />
            <Footer />
          </Route>
          <Route exact path="/dashboard">
            <Header />
            <Helmet>
              <title>
                Clarity AI Dashboard - Upscale & Enhance your Images
              </title>
              <meta
                name="description"
                content="Access your Clarity AI dashboard to manage your account and settings. Utilize the leading AI technology for image upscaling and enhancement, all from one central location."
              />
              <link rel="canonical" href="https://clarityai.co/dashboard" />
            </Helmet>
            <Dashboard />
          </Route>
          <Route exact path="/api">
            <Helmet>
              <title>API Image Upscaler & Enhancer - Clarity AI</title>
              <meta
                name="description"
                content="API to upscale & enhance images. Business API to get high resolution images. Benefit from permanent output files, simple parameters, optimized presets, and fast prediction times."
              />
              <link rel="canonical" href="https://clarityai.co/api" />
            </Helmet>
            <Header />
            <Docs />
          </Route>
          <Route exact path="/comfyui">
            <Helmet>
              <title>ComfyUI Upscaler & Enhancer - Clarity AI Plugin</title>
              <meta
                name="description"
                content="The ComfyUI for Clarity AI. Enhance and upscale your images with ease using our AI-driven solutions, optimized for high resolution and perfect results."
              />
              <link rel="canonical" href="https://clarityai.co/comfyui" />
            </Helmet>
            <Header />
            <ComfyUI />
          </Route>
          <Route exact path="/pricing">
            <Helmet>
              <title>Pricing - Clarity AI Image Upscaler & Enhancer</title>
              <meta
                name="description"
                content="Explore Clarity AI's flexible pricing options for our advanced image upscaler and enhancer. Choose from subscription-based plans or one-off purchases to suit your needs."
              />
              <link rel="canonical" href="https://clarityai.co/pricing" />
            </Helmet>
            <Header />
            <Pricing />
            <Footer />
          </Route>
          <Route exact path="/success">
            <Header />
            <Success />
          </Route>
          <Route exact path="/billing">
            <Header />
            <Helmet>
              <title>Billing - Clarity AI</title>
              <meta
                name="description"
                content="Manage your billing settings for Clarity AI. Change your plan, cancel your subscription, or get support."
              />
              <link rel="canonical" href="https://clarityai.co/billing" />
            </Helmet>
            <Billing />
          </Route>
          <Route exact path="/support">
            <Helmet>
              <title>Support - Clarity AI</title>
              <meta
                name="description"
                content="Get support for Clarity AI. Contact me for any issues or questions."
              />
              <link rel="canonical" href="https://clarityai.co/support" />
            </Helmet>
            <Header />
            <Support />
          </Route>
          <Route exact path="/profile">
            <Helmet>
              <title>Profile Settings - Clarity AI</title>
              <meta
                name="description"
                content="Manage your Clarity AI profile settings, API keys, billing preferences, and account information."
              />
              <link rel="canonical" href="https://clarityai.co/profile" />
            </Helmet>
            <Header />
            <Profile />
          </Route>
          <Route exact path="/feedback">
            <Helmet>
              <title>Feedback - Clarity AI</title>
              <meta
                name="description"
                content="Share your feedback about Clarity AI. Help us improve our services."
              />
              <link rel="canonical" href="https://clarityai.co/feedback" />
            </Helmet>
            <Header />
            <Feedback />
          </Route>
          <PrivateRoute exact path="/admin">
            <Admin />
          </PrivateRoute>
          <PrivateRoute exact path="/admin-dashboard">
            <Header />
            <AdminDashboard />
          </PrivateRoute>
          <PrivateRoute exact path="/admin/predictions">
            <Admin initialTab="predictions" />
          </PrivateRoute>
          <PrivateRoute exact path="/stats">
            <Header />
            <Stats />
          </PrivateRoute>
          <PrivateRoute exact path="/video-test">
            <Header />
            <VideoTest />
          </PrivateRoute>
          <PrivateRoute exact path="/admin-tools">
            <AdminTools />
          </PrivateRoute>
          <Route exact path="/anime-image-upscaling">
            <Helmet>
              <title>
                Anime Image Upscaling - Clarity AI - #1 AI Anime Upscaler
              </title>
              <meta
                name="description"
                content="Upscale and enhance your anime images with Clarity AI. Our advanced AI technology preserves the unique style of anime while increasing resolution and detail."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/anime-image-upscaling"
              />
            </Helmet>
            <Header />
            <Popup />
            <Hero keyword="anime-image-upscaling" />
            <Features keyword="anime-image-upscaling" />
            <Examples keyword="anime-image-upscaling" />
            <FAQs keyword="anime-image-upscaling" />
            <CalltoAction keyword="anime-image-upscaling" />
            <Footer />
          </Route>
          <Route exact path="/tools/high-resolution-image-converter">
            <Helmet>
              <title>
                High Resolution Image Converter - HD 4k Image Converter
              </title>
              <meta
                name="description"
                content="Easily increase image resolution for impressive results. Convert low-resolution images into high-definition works of art with our AI-driven tool."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/tools/high-resolution-image-converter"
              />
            </Helmet>
            <Header />
            <ImageConverter />
            <ContactButton />
            <Footer />
          </Route>
          <Route exact path="/tools/image-description-generator">
            <Helmet>
              <title>Free AI Image Description Generator | No Login</title>
              <meta
                name="description"
                content="Image Description Generator. Upload an image and we will grab the content and generate a description with AI. Free to use."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/tools/image-description-generator"
              />
            </Helmet>
            <Header />
            <ImageDescriptionGenerator />
            <ContactButton />
            <Footer />
          </Route>
          <Route exact path="/flux-upscaler">
            <Helmet>
              <title>
                Flux Upscaler - AI Image Upscaling with Flux Model | Clarity AI
              </title>
              <meta
                name="description"
                content="Transform your images with the Flux AI Upscaler. Compatible with Flux.1-dev-controlnet-upscaler, ComfyUI and other Flux workflows. The best upscaler for Flux available online."
              />
              <link rel="canonical" href="https://clarityai.co/flux-upscaler" />
            </Helmet>
            <Header />
            <FluxUpscaler />
            <ContactButton />
            <Footer />
          </Route>
          <Route exact path="/clarity-pro-upscaler">
            <Helmet>
              <title>
                Clarity Pro Upscaler - Professional AI Image Upscaler | Clarity
                AI
              </title>
              <meta
                name="description"
                content="The Clarity Pro Upscaler is a professional-grade AI image upscaler. Enhance and upscale your photos with state-of-the-art quality - made for photographers, designers and studios."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/clarity-pro-upscaler"
              />
            </Helmet>
            <Header />
            <ClarityProUpscaler />
            <ContactButton />
            <Footer />
          </Route>
          <Route exact path="/clarity-upscaler">
            <Helmet>
              <title>
                Clarity Upscaler - AI Image Upscaler & Enhancer | Clarity AI
              </title>
              <meta
                name="description"
                content="The Clarity Upscaler is the AI image upscaler trusted by creators worldwide. Upscale and enhance any image to HD, 4K and beyond with sharp, natural results."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/clarity-upscaler"
              />
            </Helmet>
            <Header />
            <ClarityUpscaler />
            <ContactButton />
            <Footer />
          </Route>
          <Route exact path="/crystal-upscaler">
            <Helmet>
              <title>
                Crystal Upscaler - Crystal Clear AI Image Upscaling | Clarity
                AI
              </title>
              <meta
                name="description"
                content="Crystal Upscaler delivers crystal clear AI image upscaling. Turn blurry photos into sharp, high-resolution images with stunning detail in seconds."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/crystal-upscaler"
              />
            </Helmet>
            <Header />
            <CrystalUpscaler />
            <ContactButton />
            <Footer />
          </Route>
          <Route exact path="/terms">
            <Helmet>
              <title>Terms of Service - Clarity AI</title>
              <meta
                name="description"
                content="The Terms of Service for Clarity AI, the AI image upscaler and enhancer with API support."
              />
              <link rel="canonical" href="https://clarityai.co/terms" />
            </Helmet>
            <Header />
            <Terms />
            <Footer />
          </Route>
          <Route exact path="/privacy">
            <Helmet>
              <title>Privacy Policy - Clarity AI</title>
              <meta
                name="description"
                content="The Privacy Policy for Clarity AI, the AI image upscaler and enhancer with API support."
              />
              <link rel="canonical" href="https://clarityai.co/privacy" />
            </Helmet>
            <Header />
            <Privacy />
            <Footer />
          </Route>
          <Route exact path="/subprocessors">
            <Helmet>
              <title>Subprocessors - Clarity AI</title>
              <meta
                name="description"
                content="The subprocessors Clarity AI uses to process customer content, as referenced in our Data Processing Agreements."
              />
              <link rel="canonical" href="https://clarityai.co/subprocessors" />
            </Helmet>
            <Header />
            <Subprocessors />
            <Footer />
          </Route>
          <Route exact path="/benchmarks/fastest-image-upscaling-api">
            <Helmet>
              <title>
                Fastest Image Upscaling API: 4K Speed Benchmark (Replicate, fal)
              </title>
              <meta
                name="description"
                content="How fast are image upscaling APIs? Median time for a 4K upscale (1920x1080 to 3840x2160) of 50 models on Replicate and fal, with method and every run."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/benchmarks/fastest-image-upscaling-api"
              />
            </Helmet>
            <Header />
            <SpeedBenchmark />
            <Footer />
          </Route>
          <Route exact path="/benchmarks/image-upscaling-api-max-resolution">
            <Helmet>
              <title>
                How Large Can Image Upscaling APIs Upscale a Photo? Max Output Resolution Benchmark
              </title>
              <meta
                name="description"
                content="How large can you upscale a normal photo? Largest output each of 62 image upscaling APIs on Replicate and fal produces from a 1 MP photo, from API limits and test runs. Plain resizing not counted."
              />
              <link
                rel="canonical"
                href="https://clarityai.co/benchmarks/image-upscaling-api-max-resolution"
              />
            </Helmet>
            <Header />
            <ResolutionBenchmark />
            <Footer />
          </Route>
          <PrivateRoute exact path="/development">
            <Development />
          </PrivateRoute>
          <Route path="*">
            <Header />
            <NotFound />
          </Route>
        </Switch>
      </Suspense>
      <Suspense fallback={<div></div>}>
        <Popup show={showLoginPopup} onClose={() => setShowLoginPopup(false)} />
      </Suspense>
      <Route exact path="/">
        <ContactButton />
      </Route>
    </div>
  )
}

export default App
